import { useEffect, useRef, useState } from "react";
import * as anagraficaApi from "../../api/anagrafica";
import { SedeSuggestion } from "../../api/anagrafica";
import { Input } from "./Input";

interface SedeAutocompleteProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Sede attualmente collegata ("" = nessuna). Insieme a onSedeIdChange rende
      il componente proprietario della chiave: vedi il commento sotto. */
  sedeId: string;
  onSedeIdChange: (sedeId: string) => void;
  /** Chiamato quando l'utente sceglie un suggerimento — restituisce anche
      l'azienda madre, così il chiamante può precompilare/aggiornare cliente_id. */
  onSelect?: (sede: SedeSuggestion) => void;
  /** Quando il testo digitato non viene salvato da nessuna parte e serve solo a
      trovare la sede, la sede scelta si mostra come chip con la X invece che
      come testo modificabile. Vedi il commento sotto. */
  soloSelezione?: boolean;
  placeholder?: string;
}

/**
 * Come si chiama l'azienda madre nella riga del suggerimento.
 *
 * L'insegna sta davanti perché è quella che si cerca: la ricerca guarda anche
 * lì (vedi `/api/sedi-search`), e prima qui compariva la sola ragione sociale
 * — chi scriveva «Burger King» si vedeva proporre «Ristorazione Micca Duemila
 * S.r.l.», cioè una riga in cui il testo digitato non c'era, e la prendeva per
 * un risultato sbagliato.
 *
 * La ragione sociale resta comunque, dopo: è ciò che distingue due punti
 * vendita della stessa catena appartenenti a società diverse, ed è il motivo
 * per cui l'azienda madre viene mostrata fin dall'inizio.
 *
 * Quando le due coincidono se ne scrive una sola: ripeterla farebbe sembrare
 * due nomi diversi ciò che è lo stesso.
 */
function azienda(s: SedeSuggestion): string {
  const insegna = s.cliente_insegna?.trim();
  if (!insegna || insegna.toLowerCase() === s.cliente_nome.trim().toLowerCase()) return s.cliente_nome;
  return `${insegna} · ${s.cliente_nome}`;
}

/**
 * Suggerisce sedi già presenti in anagrafica mentre si scrive, mostrando a
 * quale azienda madre appartiene ciascuna — utile proprio quando esistono
 * più sedi con nomi simili sotto aziende diverse. Scegliendo un suggerimento
 * diverso da quello attuale, la sede risulta "spostata" sotto la nuova
 * azienda madre (per il record in modifica, non cambia la sede stessa nel
 * database finché non si salva il form).
 *
 * ## Perché la chiave la gestisce il componente e non i form
 *
 * Prima ogni form decideva da sé quando azzerare `sede_id`, e i quattro che
 * usano questo componente decidevano cose diverse: due non lo azzeravano mai,
 * due solo a campo completamente vuoto. Risultato comune a tutti: chi
 * sceglieva una sede dalla tendina e poi **modificava il testo** si teneva la
 * chiave vecchia, e il record finiva collegato a una sede diversa da quella
 * scritta. Con la coppia `sedeId` + `onSedeIdChange` la regola sta qui, in un
 * posto solo, e un form nuovo non può dimenticarsela.
 *
 * ## Perché l'azzeramento è legato alla digitazione e non a un confronto
 *
 * Sembrerebbe equivalente azzerare quando il testo non coincide più col nome
 * della sede scelta. Non lo è: `sede_id` viene impostato anche per vie
 * programmatiche in cui il testo legittimamente NON coincide — il suggerimento
 * "match sede" di Installazioni scrive la chiave lasciando l'insegna com'è, la
 * precompilazione da scheda fa lo stesso su Interventi e Attivazioni, e ogni
 * form di modifica carica chiave e testo insieme dal record. Un confronto li
 * azzererebbe tutti al primo render. La chiave stalla nasce solo dal digitare,
 * ed è lì che va intercettata: `onChange` dell'input, non un `useEffect`.
 *
 * ## Perché `soloSelezione` non vale per tutti
 *
 * Su Interventi e Sopralluoghi il testo digitato non viene salvato da nessuna
 * parte (non esiste una colonna che lo accolga): serve solo a trovare la sede.
 * Lì la chiave, cadendo, sparirebbe in silenzio senza che a schermo cambi
 * nulla — quindi la sede scelta si mostra come chip con la X, e lo stato è
 * visibile invece che deducibile. Su Attivazioni e Installazioni invece il
 * testo è una colonna vera (`sede`, `insegna`) con un significato proprio —
 * su Installazioni l'insegna può legittimamente differire dal nome della
 * sede — e bloccarlo sarebbe una regressione.
 */
export function SedeAutocomplete({
  label,
  value,
  onChange,
  sedeId,
  onSedeIdChange,
  onSelect,
  soloSelezione,
  placeholder,
}: SedeAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<SedeSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (value.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { sedi } = await anagraficaApi.searchSedi(value);
        setSuggestions(sedi);
        setOpen(sedi.length > 0);
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSelect(sede: SedeSuggestion) {
    onChange(sede.nome_sede ?? "");
    onSedeIdChange(sede.id);
    onSelect?.(sede);
    setOpen(false);
  }

  function handleDeseleziona() {
    onChange("");
    onSedeIdChange("");
    setSuggestions([]);
    setOpen(false);
  }

  // Chip: la sede è scelta e il testo non serve più: mostrarlo modificabile
  // inviterebbe a correggerlo, e ogni correzione scollegherebbe la sede.
  if (soloSelezione && sedeId) {
    return (
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-ink">{label}</label>
        <div className="flex items-center justify-between gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
          <span className="text-sm text-ink">{value || "Sede collegata"}</span>
          <button
            type="button"
            onClick={handleDeseleziona}
            aria-label="Scollega la sede"
            className="shrink-0 rounded px-1.5 text-lg leading-none text-ink/40 hover:text-ink"
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        label={label}
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          // Digitare scollega sempre: da qui in poi il testo non descrive più
          // la sede scelta, e tenerne la chiave la renderebbe una bugia.
          if (sedeId) onSedeIdChange("");
          setOpen(true);
        }}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-line bg-white shadow-lg">
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => handleSelect(s)}
              className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-primary/5"
            >
              <span className="font-medium text-ink">{s.nome_sede ?? "Sede senza nome"}</span>
              <span className="text-xs text-ink/50">
                {azienda(s)}
                {(s.indirizzo || s.citta) && ` — ${[s.indirizzo, s.citta].filter(Boolean).join(", ")}`}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
